<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { useNavigation, mobileTabItems, mobileOverflowItems } from '@/composables/useNavigation'
import { useAuthStore } from '@/features/account/session'
import { useMyProfileStore } from '@/features/account/profile'
import BaseImage from '@/components/ui/BaseImage.vue'
import storytellerLogo from '@/assets/icons/Storyteller.svg'

const { isActive, isOverflowRouteActive } = useNavigation()
const authStore = useAuthStore()
const myProfileStore = useMyProfileStore()
const route = useRoute()

const leftTabs = mobileTabItems.slice(0, 2)
const rightTabs = mobileTabItems.slice(2)

const profileTo = computed(() => authStore.userId ? `/user/${authStore.userId}` : '/')
const isProfileActive = computed(() => route.path.startsWith('/user/') && route.params?.id === authStore.userId)
</script>

<template>
  <nav class="bottom-tab-bar" aria-label="Main navigation">
    <!-- Left tabs: Home, Characters -->
    <RouterLink
      v-for="item in leftTabs"
      :key="item.to"
      :to="item.to"
      class="bottom-tab-bar__item"
      :class="{ 'bottom-tab-bar__item--active': isActive(item) }"
      :aria-label="item.label"
      :aria-current="isActive(item) ? 'page' : undefined"
    >
      <component :is="item.icon" class="bottom-tab-bar__icon" aria-hidden="true" />
    </RouterLink>

    <!-- Center: Storyteller overflow button -->
    <Popover v-slot="{ open }" class="bottom-tab-bar__more-wrapper pt-3 px-5">
      <PopoverButton
        class="bottom-tab-bar__item bottom-tab-bar__more-btn"
        :class="{
          'bottom-tab-bar__item--active': isOverflowRouteActive && !open,
          'bottom-tab-bar__more-btn--open': open,
        }"
        aria-label="More navigation"
      >
        <img
          :src="storytellerLogo"
          alt=""
          class="bottom-tab-bar__storyteller-icon"
          aria-hidden="true"
        />
      </PopoverButton>

      <Transition
        enter-active-class="bottom-tab-bar__overflow-enter-active"
        enter-from-class="bottom-tab-bar__overflow-enter-from"
        enter-to-class="bottom-tab-bar__overflow-enter-to"
        leave-active-class="bottom-tab-bar__overflow-leave-active"
        leave-from-class="bottom-tab-bar__overflow-leave-from"
        leave-to-class="bottom-tab-bar__overflow-leave-to"
      >
        <PopoverPanel v-slot="{ close }" class="bottom-tab-bar__overflow-panel" role="menu">
          <RouterLink
            v-for="item in mobileOverflowItems"
            :key="item.to"
            :to="item.to"
            class="bottom-tab-bar__overflow-item"
            :class="{ 'bottom-tab-bar__overflow-item--active': isActive(item) }"
            role="menuitem"
            :aria-current="isActive(item) ? 'page' : undefined"
            @click="close"
          >
            <component :is="item.icon" class="bottom-tab-bar__overflow-icon" aria-hidden="true" />
            <span>{{ item.label }}</span>
          </RouterLink>
        </PopoverPanel>
      </Transition>
    </Popover>

    <!-- Right tabs: Conversations -->
    <RouterLink
      v-for="item in rightTabs"
      :key="item.to"
      :to="item.to"
      class="bottom-tab-bar__item"
      :class="{ 'bottom-tab-bar__item--active': isActive(item) }"
      :aria-label="item.label"
      :aria-current="isActive(item) ? 'page' : undefined"
    >
      <component :is="item.icon" class="bottom-tab-bar__icon" aria-hidden="true" />
    </RouterLink>

    <!-- Profile tab -->
    <RouterLink
      :to="profileTo"
      class="bottom-tab-bar__item"
      :class="{ 'bottom-tab-bar__item--active': isProfileActive }"
      aria-label="Profile"
      :aria-current="isProfileActive ? 'page' : undefined"
    >
      <span class="bottom-tab-bar__icon bottom-tab-bar__avatar">
        <BaseImage
          :src="myProfileStore.avatarUrl"
          :alt="myProfileStore.username || 'Profile'"
          :image-focus="myProfileStore.avatarFocus"
          fallback-size="sm"
        />
      </span>
    </RouterLink>
  </nav>
</template>

<style scoped>
.bottom-tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(56px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 900;
  display: flex;
  background: var(--theme-surface-solid);
  backdrop-filter: blur(12px);
  border-top: 1px solid var(--theme-border);
}

.bottom-tab-bar__item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  text-decoration: none;
  color: var(--theme-text-tertiary);
  min-height: 44px;
  transition: color 150ms;
}

.bottom-tab-bar__item--active {
  color: var(--theme-primary);
}

.bottom-tab-bar__item:active {
  opacity: 0.7;
}

.bottom-tab-bar__icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: inherit;
}

.bottom-tab-bar__icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.bottom-tab-bar__avatar {
  border-radius: 6px;
  overflow: hidden;
}

.bottom-tab-bar__avatar :deep(img),
.bottom-tab-bar__avatar :deep(.avatar-fallback) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Center Storyteller overflow button */
.bottom-tab-bar__more-wrapper {
  flex: 1;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
}

.bottom-tab-bar__more-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.bottom-tab-bar__more-btn--open {
  color: var(--theme-primary);
}

.bottom-tab-bar__storyteller-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
  transition: opacity 150ms;
}

.bottom-tab-bar__more-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--theme-surface-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 150ms;
}

.bottom-tab-bar__more-btn--open .bottom-tab-bar__more-circle,
.bottom-tab-bar__item--active .bottom-tab-bar__more-circle {
  background: rgba(29, 138, 255, 0.18);
}


/* Overflow panel */
.bottom-tab-bar__overflow-panel {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  background: var(--theme-surface-solid);
  border: 1px solid var(--theme-border-hover);
  border-radius: 12px;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.35);
  padding: 6px;
  z-index: 910;
  outline: none;
}

.bottom-tab-bar__overflow-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--theme-text-secondary);
  font-size: 14px;
  font-weight: 500;
  transition: background-color 150ms, color 150ms;
}

.bottom-tab-bar__overflow-item:hover,
.bottom-tab-bar__overflow-item:focus-visible {
  background: var(--theme-surface-interactive);
  color: var(--theme-on-surface);
}

.bottom-tab-bar__overflow-item--active {
  color: var(--theme-primary);
}

.bottom-tab-bar__overflow-icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: inherit;
}

.bottom-tab-bar__overflow-icon :deep(svg) {
  width: 100%;
  height: 100%;
}

/* Transitions */
.bottom-tab-bar__overflow-enter-active,
.bottom-tab-bar__overflow-leave-active {
  transition: opacity 120ms, transform 120ms;
}

.bottom-tab-bar__overflow-enter-from,
.bottom-tab-bar__overflow-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px) scale(0.97);
}

.bottom-tab-bar__overflow-enter-to,
.bottom-tab-bar__overflow-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0) scale(1);
}
</style>
